import { useCallback, useEffect, useMemo, useState } from "react";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Button, Chip, HeaderBar, TableComponent, TypoGraph } from "components";
import { CompanyFilterDialog } from "components/dialog";
import { getAmountIntegrityRows } from "api";
import { IAmountIntegrityRow } from "interfaces/IAmountIntegrityRow";

type FilterMode = "all" | "mismatches";

const AmountIntegrityPage = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<IAmountIntegrityRow[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [filter, setFilter] = useState<FilterMode>("all");
  const [openCompanyModal, setOpenCompanyModal] = useState(false);
  const [selectedCompaniesId, setSelectedCompaniesId] = useState<number[]>([]);
  const [companiesFilter, setCompaniesFilter] = useState<number[]>([]);

  const loadRows = useCallback(() => {
    setLoading(true);
    getAmountIntegrityRows(companiesFilter)
      .then((res) => {
        const mapped = (res.data || []).map((row) => ({
          ...row,
          id: `${row.paymentType}-${row.entityId}-${row.provider}`,
          status:
            row.providerMismatch || row.rawMismatch ? "mismatch" : "ok",
        }));
        setRows(mapped);
      })
      .finally(() => setLoading(false));
  }, [companiesFilter]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const toggleOption = useCallback(
    (option: number) => {
      if (selectedCompaniesId.includes(option)) {
        setSelectedCompaniesId(
          selectedCompaniesId.filter((o) => o !== option)
        );
      } else {
        setSelectedCompaniesId([...selectedCompaniesId, option]);
      }
    },
    [selectedCompaniesId]
  );

  const handleApplyCompanyFilter = () => {
    setCompaniesFilter(selectedCompaniesId);
    setOpenCompanyModal(false);
  };

  const onCleanFilter = () => {
    setSelectedCompaniesId([]);
    setCompaniesFilter([]);
  };

  const filteredRows = useMemo(() => {
    let next = rows;
    if (filter === "mismatches") {
      next = next.filter((r) => r.providerMismatch || r.rawMismatch);
    }
    const q = searchValue.trim().toLowerCase();
    if (!q) {
      return next;
    }
    return next.filter((row) =>
      [
        row.paymentType,
        row.entityId,
        row.provider,
        row.destinationAccount,
        row.companyName,
        row.submittedBy,
        row.approvedBy,
        row.amountInputRaw,
        row.amountStored,
        row.amountSent,
        row.amountConfirmed,
      ]
        .filter((v) => v != null)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, searchValue, filter]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "paymentType", headerName: "Type", width: 130 },
      { field: "entityId", headerName: "ID", width: 90 },
      { field: "provider", headerName: "Provider", width: 110 },
      {
        field: "destinationAccount",
        headerName: "Destination",
        width: 160,
        valueGetter: (params) => params.row.destinationAccount || "—",
      },
      {
        field: "companyName",
        headerName: "Company",
        width: 160,
        valueGetter: (params) => params.row.companyName || "—",
      },
      {
        field: "submittedBy",
        headerName: "Submitted by",
        width: 150,
        valueGetter: (params) => params.row.submittedBy || "—",
      },
      {
        field: "approvedBy",
        headerName: "Approved by",
        width: 150,
        valueGetter: (params) => params.row.approvedBy || "—",
      },
      {
        field: "amountInputRaw",
        headerName: "Raw input",
        width: 110,
        valueGetter: (params) => params.row.amountInputRaw || "—",
      },
      {
        field: "amountStored",
        headerName: "Stored",
        width: 110,
        valueGetter: (params) => params.row.amountStored || "—",
      },
      {
        field: "amountSent",
        headerName: "Sent",
        width: 110,
        valueGetter: (params) => params.row.amountSent || "—",
      },
      {
        field: "amountConfirmed",
        headerName: "Confirmed",
        width: 110,
        valueGetter: (params) => params.row.amountConfirmed || "—",
      },
      {
        field: "flags",
        headerName: "Flags",
        width: 220,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IAmountIntegrityRow;
          return (
            <>
              {row.providerMismatch && (
                <Chip
                  label="Provider mismatch"
                  color="error"
                  className="errorChip"
                />
              )}
              {row.rawMismatch && (
                <Chip
                  label="Raw mismatch"
                  color="error"
                  className="errorChip"
                />
              )}
              {row.amountModified && (
                <Chip label="Amount modified" color="warning" />
              )}
              {!row.providerMismatch &&
                !row.rawMismatch &&
                !row.amountModified && (
                  <TypoGraph variant="body1" content="—" />
                )}
            </>
          );
        },
      },
    ],
    []
  );

  return (
    <>
      <HeaderBar title="Amount Integrity" />
      <div style={{ display: "flex", gap: 8, padding: "8px 12px" }}>
        {(
          [
            ["all", "All"],
            ["mismatches", "Mismatches only"],
          ] as [FilterMode, string][]
        ).map(([value, label]) => (
          <Button
            key={value}
            id={`amount-integrity-filter-${value}`}
            variant={filter === value ? "contained" : "outlined"}
            label={label}
            onClick={() => setFilter(value)}
          />
        ))}
      </div>
      <TableComponent
        title="Amount Integrity"
        columns={columns}
        data={filteredRows}
        totalCount={filteredRows.length}
        loading={loading}
        searchValue={searchValue}
        isSearchable
        serverPaginationMode={false}
        serverSortingMode={false}
        handleQuickSearch={setSearchValue}
        handleCustomFilterButton={() => setOpenCompanyModal(true)}
      />
      <CompanyFilterDialog
        open={openCompanyModal}
        onClose={() => setOpenCompanyModal(false)}
        selectedOptions={selectedCompaniesId}
        toggleOption={toggleOption}
        onCleanFilter={onCleanFilter}
        onApplyFilter={handleApplyCompanyFilter}
      />
    </>
  );
};

export default AmountIntegrityPage;
